import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPinIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
interface HotelData {
  name: string;
  image: string;
  location: string;
  price: string;
  stars: number;
  description: string;
}
interface RegionConfig {
  title: string;
  subtitle: string;
  heroImage: string;
  description: string;
  hotels: HotelData[];
}
const regions: Record<string, RegionConfig> = {
  'hotels-everest': {
    title: 'Hotels in Everest Region',
    subtitle: 'Luxury stays at the top of the world',
    heroImage: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1920&q=80',
    description: "The Everest region offers some of the most unique accommodations on Earth. From the world's highest luxury hotel in Namche Bazaar to premium mountain lodges along the trekking route, experience five-star comfort amidst the most dramatic landscape on the planet.",
    hotels: [{
      name: 'Everest View Hotel',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
      location: 'Namche Bazaar, 3,880m',
      price: '$280',
      stars: 5,
      description: "The world's highest luxury hotel with panoramic Everest views from every room."
    }, {
      name: 'Yeti Mountain Home',
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80',
      location: 'Tengboche, 3,860m',
      price: '$220',
      stars: 4,
      description: 'Premium lodge with heated rooms, hot showers, and stunning Ama Dablam views.'
    }, {
      name: 'Khumbu Lodge',
      image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=600&q=80',
      location: 'Phakding, 2,610m',
      price: '$150',
      stars: 4,
      description: 'Comfortable riverside lodge with traditional Sherpa hospitality and modern amenities.'
    }, {
      name: 'Himalayan Lodge',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
      location: 'Lukla, 2,860m',
      price: '$180',
      stars: 4,
      description: 'Gateway lodge with comfortable rooms, restaurant, and trekking preparation facilities.'
    }]
  },
  'hotels-pokhara': {
    title: 'Hotels in Pokhara',
    subtitle: 'Lakeside luxury with mountain views',
    heroImage: 'https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=1920&q=80',
    description: "Pokhara, the jewel of Nepal's lake district, offers world-class luxury hotels set against the backdrop of the Annapurna range. Wake up to reflections of snow-capped peaks in Phewa Lake, enjoy spa treatments with mountain views, and experience the perfect blend of adventure and relaxation.",
    hotels: [{
      name: 'Pavilions Himalayas',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
      location: 'Lakeside, Pokhara',
      price: '$420',
      stars: 5,
      description: 'Eco-luxury resort with private villas, infinity pool, and unobstructed Annapurna views.'
    }, {
      name: 'Tiger Mountain Lodge',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
      location: 'Pokhara Hills',
      price: '$350',
      stars: 5,
      description: 'Boutique hilltop lodge surrounded by forests with panoramic Himalayan vistas.'
    }, {
      name: 'Temple Tree Resort',
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80',
      location: 'Lakeside, Pokhara',
      price: '$250',
      stars: 4,
      description: 'Elegant lakeside resort with lush gardens, pool, and fine dining restaurant.'
    }, {
      name: 'Fishtail Lodge',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
      location: 'Phewa Lake Island',
      price: '$300',
      stars: 5,
      description: 'Iconic island lodge accessible only by boat, offering serene lakefront luxury.'
    }]
  },
  'hotels-nagarkot': {
    title: 'Hotels in Nagarkot',
    subtitle: 'Sunrise views over the Himalayas',
    heroImage: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1920&q=80',
    description: "Perched on a ridge at 2,175 meters, Nagarkot is famous for its spectacular sunrise views over the Himalayan range — from Dhaulagiri in the west to Everest in the east. Just 32km from Kathmandu, it's the perfect escape for travelers seeking mountain tranquility without the trek.",
    hotels: [{
      name: 'Club Himalaya',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
      location: 'Nagarkot Ridge',
      price: '$200',
      stars: 5,
      description: 'Premier hilltop resort with 360-degree mountain views, spa, and heated pool.'
    }, {
      name: 'Hotel Mystic Mountain',
      image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=600&q=80',
      location: 'Nagarkot',
      price: '$150',
      stars: 4,
      description: 'Charming mountain hotel with sunrise terrace and traditional Nepali architecture.'
    }, {
      name: 'Fort Resort',
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80',
      location: 'Nagarkot Hilltop',
      price: '$180',
      stars: 4,
      description: 'Castle-style resort with panoramic views, organic garden, and cultural programs.'
    }, {
      name: 'Nagarkot Farmhouse',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
      location: 'Nagarkot Village',
      price: '$120',
      stars: 3,
      description: 'Authentic farmhouse stay with home-cooked meals and stunning valley views.'
    }]
  }
};
interface HotelsRegionPageProps {
  regionKey: string;
  onNavigate: (page: string) => void;
}
export function HotelsRegionPage({
  regionKey,
  onNavigate
}: HotelsRegionPageProps) {
  const config = regions[regionKey];
  if (!config) return null;
  return <div className="w-full" data-id="element-1676">
      <RegionHero config={config} onNavigate={onNavigate} data-id="element-1677" />
      <RegionAbout config={config} data-id="element-1678" />
      <HotelGrid hotels={config.hotels} onNavigate={onNavigate} data-id="element-1679" />
    </div>;
}
function RegionHero({
  config,
  onNavigate
}: {
  config: RegionConfig;
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[50vh] flex items-end overflow-hidden" data-id="element-1680">
      <div className="absolute inset-0" data-id="element-1681">
        <img src={config.heroImage} alt={config.title} className="w-full h-full object-cover" data-id="element-1682" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-1683" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 pt-32" data-id="element-1684">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-1685">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-1686">
            <li data-id="element-1687">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-1688">
                Home
              </button>
            </li>
            <li data-id="element-1689">/</li>
            <li data-id="element-1690">
              <span className="hover:text-white transition-colors" data-id="element-1691">Hotels</span>
            </li>
            <li data-id="element-1692">/</li>
            <li className="text-[#D4A853]" data-id="element-1693">
              {config.title.replace('Hotels in ', '')}
            </li>
          </ol>
        </nav>
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-1694">
          {config.title.replace('Hotels in ', '')}
          <br data-id="element-1695" />
          <span className="italic font-medium text-[#D4A853]" data-id="element-1696">
            Luxury Hotels
          </span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-1697">
          {config.subtitle}
        </motion.p>
      </div>
    </section>;
}
function RegionAbout({
  config
}: {
  config: RegionConfig;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1698">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1699">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-1700">
          <div className="flex items-center gap-3 mb-6" data-id="element-1701">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1702">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1703" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1704">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-1705">
            About the Region
          </h2>
          <p className="text-slate-600 leading-relaxed text-base" data-id="element-1706">
            {config.description}
          </p>
        </motion.div>
      </div>
    </section>;
}
function HotelGrid({
  hotels,
  onNavigate
}: {
  hotels: HotelData[];
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-1707">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1708">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-1709">
          <div className="flex items-center gap-3 mb-6" data-id="element-1710">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1711">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1712" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1713">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1714">
            Featured Hotels
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1715">
            Handpicked luxury accommodations
          </p>
        </motion.div>

        <div className="space-y-6" data-id="element-1716">
          {hotels.map((hotel, i) => <motion.article key={hotel.name} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} onClick={() => onNavigate('plan-trip')} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm cursor-pointer hover:shadow-lg hover:border-teal-100 transition-all duration-300 flex flex-col sm:flex-row" data-id="element-1717">
              <div className="relative overflow-hidden sm:w-72 flex-shrink-0 aspect-[16/10] sm:aspect-auto" data-id="element-1718">
                <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1719" />
              </div>
              <div className="p-6 sm:p-8 flex flex-col justify-between flex-1" data-id="element-1720">
                <div data-id="element-1721">
                  <div className="flex items-center gap-0.5 mb-2" data-id="element-1722">
                    {Array.from({
                  length: hotel.stars
                }).map((_, s) => <StarIcon key={s} className="w-3.5 h-3.5 fill-[#D4A853] text-[#D4A853]" data-id="element-1723" />)}
                  </div>
                  <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-1.5 group-hover:text-teal-700 transition-colors" data-id="element-1724">
                    {hotel.name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-3" data-id="element-1725">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-1726" />
                    <span data-id="element-1727">{hotel.location}</span>
                  </div>
                  <p className="text-slate-500 text-base leading-relaxed" data-id="element-1728">
                    {hotel.description}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-5 pt-5 border-t border-slate-100" data-id="element-1729">
                  <div data-id="element-1730">
                    <span className="text-teal-600 text-lg font-semibold" data-id="element-1731">
                      {hotel.price}
                    </span>
                    <span className="text-slate-400 text-sm" data-id="element-1732"> / night</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-teal-600 transition-colors" data-id="element-1733">
                    View Details
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1734" />
                  </span>
                </div>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}